// @flow

import React, { PureComponent } from 'react';
import type { Element, ElementRef } from 'react';
import PropTypes from 'prop-types';

// Components
import Button from 'Common/Button';
// Helpers
import styles from 'components/Accordion.scss';
import Icon from 'Common/Icon';
import TagHtml from '../TagHtml';
import classNames from 'classnames';
import { gtmEventPush } from 'utils/gtm/events';

type DefaultProps = {|
  accordionControl: string,
  accordionHeight: number,
  accordionTitleTag: string,
  allowHtml: boolean,
  toggleTitles: Array<empty>
|};

/**
 * Accordion React component.
 */
class Accordion extends PureComponent<*, *> {
  /**
   *  Props implementation.
   */
  static propTypes = {
    accordionHeight: PropTypes.number,
    accordionControl: PropTypes.string,
    accordionTitle: PropTypes.string,
    accordionTitleTag: PropTypes.string,
    content: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.string,
      PropTypes.array
    ]),
    allowHtml: PropTypes.bool,
    children: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.array,
      PropTypes.string
    ]),
    extendStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    toggleTitles: PropTypes.array,
    gtmSectionName: PropTypes.string.isRequired
  };

  /**
   *  Default Props implementation.
   */
  static defaultProps: DefaultProps = {
    accordionHeight: 80,
    accordionControl: 'button',
    accordionTitleTag: 'h4',
    allowHtml: false,
    toggleTitles: []
  };

  tagHtmlRef: any;

  /**
   * Props implementation.
   * @param {object} props - component props
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      isOpen: false
    };
  }

  /**
   * componentDidUpdate implementation.
   * @param {object} prevProps - component props
   * @param {object} prevState - component state
   */
  componentDidUpdate(prevProps: Object, prevState: Object) {
    let height = this.tagHtmlRef && this.tagHtmlRef.offsetHeight;
    if (this.state.height !== height) {
      this.setState({ height });
    }
  }

  /**
   * ToggleAccordion implementation.
   */
  toggleAccordion: () => void = () => {
    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        isOpen: !this.state.isOpen
      }
    });
    this.setState({ isOpen: !this.state.isOpen });
  };

  setRef: (el: null | ElementRef<string>) => void = el => {
    this.tagHtmlRef = el;
  };

  /**
   * Render
   * @return {ReactElement} markup
   */
  render(): Element<*> | null {
    const {
      classAccordionContainer,
      classAccordion,
      classAccordionButton,
      classAccordionContent,
      classAccordionTitle
    } = styles;

    const {
      accordionTitle,
      accordionHeight,
      allowHtml,
      children,
      extendStyle,
      toggleTitles
    } = this.props;
    const TagElem = this.props.accordionTitleTag;
    const contentToRender = allowHtml ? (
      <TagHtml content={children || ''} />
    ) : (
      this.props.children
    );
    if (!contentToRender) {
      return null;
    }

    return (
      <div className={classNames([extendStyle, classAccordionContainer])}>
        <TagElem
          onClick={
            this.props.accordionControl === 'arrow'
              ? this.toggleAccordion
              : null
          }
          className={classAccordionTitle}
        >
          {accordionTitle}
          {this.props.accordionControl === 'arrow' && (
            <Icon iconName={this.state.isOpen ? 'angle-up' : 'angle-down'} />
          )}
        </TagElem>

        <div
          className={[classAccordion]}
          style={{
            height: this.state.isOpen ? this.state.height : accordionHeight
          }}
        >
          <div className={classAccordionContent} ref={this.setRef}>
            {contentToRender}
          </div>
        </div>
        {this.state.height > accordionHeight &&
          this.props.accordionControl === 'button' && (
            <Button
              dataSection="browse-all-used-makes"
              extendStyle={classAccordionButton}
              buttonText={this.state.isOpen ? 'Read less' : 'Read more'}
              onClick={this.toggleAccordion}
            />
          )}
        {this.state.height > accordionHeight &&
          this.props.accordionControl === 'toggle' && (
            <div className={styles.toggleControl}>
              <hr />
              <div onClick={this.toggleAccordion} className={styles.toggleText}>
                <span>
                  {this.state.isOpen ? toggleTitles[1] : toggleTitles[0]}
                </span>
                <Icon
                  iconName={this.state.isOpen ? 'angle-up' : 'angle-down'}
                />
              </div>
            </div>
          )}
      </div>
    );
  }
}

export default Accordion;



// WEBPACK FOOTER //
// ./src/Common/Accordion/index.js